<template>
  <v-card data-testid="add-template-dialog">
    <v-card-title class="white--text primary headline">
      {{ title }}
      <v-icon v-if="isPenalty" class="ml-2 mt-n1" color="white">{{ weightAlert.path }}</v-icon>
    </v-card-title>
    <v-card-text>
      <FormWrapper @submit="saveBtnClicked" :disabled="!hasValidSelection">
        <v-container class="mt-4">
          <v-row class="d-flex align-stretch">
            <v-col class="pb-0" cols="6">
              <v-card class="mx-4 mb-6 pa-4" style="height: 90%">
                <v-card-title class="pa-0">
                  {{ $t('selectCounterparty') }}
                </v-card-title>
                <v-select
                  v-model="selectedCounterpartyType"
                  :items="[
                    { text: $t('account'), value: COUNTERPARTY_TYPE_ACCOUNT },
                    { text: $t('runtimeField'), value: COUNTERPARTY_TYPE_RUNTIME_FIELD }
                  ]"
                />
                <AccountAutocomplete
                  v-if="selectedCounterpartyType === COUNTERPARTY_TYPE_ACCOUNT"
                  :accountId="selectedAccount?.accountId"
                  :hint="$t('specifyCounterpartyActivity')"
                  clearable
                  unsavedTags
                  chipStyle="below"
                  @account-chosen="selectAccount"
                  :fetchTagsAndAccounts="false"
                />
                <v-radio-group
                v-if="selectedCounterpartyType === COUNTERPARTY_TYPE_RUNTIME_FIELD"
                v-model="selectedRuntimeField">
                  <v-radio
                  v-for="accountType in tractAccountTypes"
                  :key="accountType.value"
                  :label="accountType.name"
                  :value="accountType.value"
                  />
                </v-radio-group>
              </v-card>
            </v-col>
            <v-col class="pb-0" cols="6">
              <v-card class="mx-4 mb-6 pa-4"  style="height: 90%">
                <v-card-title class="pa-0">
                  {{ $t('defaultRecoveryMode') }}
                  <Icon
                    v-if="hideDefaultRecoveryModeOptions"
                    icon="mdi-block-helper"
                    iconColor="grey"
                    class="ml-1 lh-0"
                    :tooltipText="defaultRecoveryModeDisabledExplanation"
                  />
                </v-card-title>
                <v-row>
                  <v-col>
                    <v-radio-group
                    v-model="selectedDefaultRecoveryMode"
                    :disabled="hideDefaultRecoveryModeOptions">
                      <v-radio
                        v-for="recoveryMode in mappedRecoveryModes()"
                        :key="recoveryMode.value"
                        :label="recoveryMode.name"
                        :value="recoveryMode.value"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>
            <v-checkbox
            class="mt-n5 ml-4"
            v-if="includeBaseCost && !paysOnVolume"
            v-model="limitToBaseRate"
            :label="$t('limitToBaseRate')"
            />
        </v-container>
        <template #left-action>
          <v-btn
            class="black--text"
            data-testid="cancel-template"
            text
            outlined
            @click="$emit('cancel')"
          >
            {{ $t('cancel') }}
          </v-btn>
        </template>
      </FormWrapper>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { RuntimeCounterparty, AccountingCategory, TemplateSpecialization, RecoveryMode } from '@/utils/Enumerations.js'
import { TemplateSpecializationInfo } from '../../activity/ActivityMapping'
import { weightAlert } from '@/images/AngelSvgLibrary'
import { PayBy } from '../../../utils/Enumerations'

const COUNTERPARTY_TYPE_ACCOUNT = 0
const COUNTERPARTY_TYPE_RUNTIME_FIELD = 1

export default {
  name: 'SelectCounterpartyDialog',

  components: {
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    AccountAutocomplete: () => import('@/components/autocomplete/AccountAutocomplete.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    propBuilder: {
      type: Object,
      required: false,
      default: undefined
    },
    newTemplateRequest: {
      type: Object,
      required: false,
      default: undefined
    }
  },

  data: () => ({
    COUNTERPARTY_TYPE_ACCOUNT,
    COUNTERPARTY_TYPE_RUNTIME_FIELD,
    weightAlert,
    TemplateSpecializationInfo,
    TemplateSpecialization,
    RecoveryMode,
    selectedAccount: undefined,
    limitToBaseRate: false,
    selectedRuntimeField: undefined,
    selectedDefaultRecoveryMode: undefined,
    selectedCounterpartyType: COUNTERPARTY_TYPE_ACCOUNT
  }),

  computed: {
    ...mapGetters('account', ['allAccounts']),

    tractAccountTypes () {
      return RuntimeCounterparty.enums
        // TODO: Remove filter when runtime landowner accruals are supported
        .filter(e => !this.isAccrual || e.label !== 'tractLandowner')
    },

    translatedTractAccountTypes () {
      return this.tractAccountTypes.map(type => ({
        text: this.$t(type.label),
        value: type.value
      }))
    },

    hasAdvances () {
      return this.selectedAccount?.hasAdvances
    },

    isDynamicCounterparty () {
      return this.selectedRuntimeField !== undefined && this.selectedRuntimeField !== null
    },

    isPayable () {
      return this.newTemplateRequest?.category === AccountingCategory.Payable.value || this.propBuilder?.category === AccountingCategory.Payable.value
    },

    isAccrual () {
      return this.newTemplateRequest?.category === AccountingCategory.Accrual.value || this.propBuilder?.category === AccountingCategory.Accrual.value
    },

    paysOnVolume () {
      return this.newTemplateRequest?.payBy === PayBy.Volume.value || this.propBuilder?.payBy === PayBy.Volume.value
    },

    currentSelection () {
      return {
        account: (this.selectedCounterpartyType === COUNTERPARTY_TYPE_ACCOUNT) ? this.selectedAccount : undefined,
        runtimeField: (this.selectedCounterpartyType === COUNTERPARTY_TYPE_RUNTIME_FIELD) ? this.selectedRuntimeField : undefined,
        defaultRecoveryMode: this.selectedDefaultRecoveryMode
      }
    },

    hasValidSelection () {
      return (this.selectedCounterpartyType === COUNTERPARTY_TYPE_RUNTIME_FIELD) ? this.currentSelection.runtimeField !== undefined : true
    },

    title () {
      const templateName = (this.propBuilder)
        ? this.propBuilder.activityTemplateName
        : this.newTemplateRequest.activityTemplateName
      return (templateName)
        ? this.$t(this.propBuilder ? 'editTemplateFor' : 'addBuilderTemplate', { templateName })
        : this.$t(this.propBuilder ? 'editTemplate' : 'addTemplate')
    },

    includeBaseCost () {
      const specialization = this.newTemplateRequest?.specialization ?? this.propBuilder?.specialization
      const category = this.newTemplateRequest?.category ?? this.propBuilder?.category
      return specialization !== TemplateSpecialization.Transfer.value && (specialization !== TemplateSpecialization.Penalty.value || category === AccountingCategory.Receivable.value)
    },

    isPenalty () {
      return (this.newTemplateRequest?.specialization ?? this.propBuilder?.specialization) === TemplateSpecialization.Penalty.value
    },

    defaultRecoveryModeDisabledExplanation () {
      if (!this.isPayable) {
        return this.$t('defaultRecoveryModeDisabledNotPayable')
      } else if (this.isPenalty) {
        return this.$t('defaultRecoveryModeDisabledPenalty')
      } else if (this.selectedRuntimeField === null && this.selectedCounterpartyType === COUNTERPARTY_TYPE_RUNTIME_FIELD) {
        return this.$t('defaultRecoveryModeDisabledNoRuntimeFieldSelected')
      } else if (this.selectedAccount === null && this.selectedCounterpartyType === COUNTERPARTY_TYPE_ACCOUNT) {
        return this.$t('defaultRecoveryModeDisabledNoAccountSelected')
      } else if (!this.isDynamicCounterparty && !this.hasAdvances) {
        return this.$t('defaultRecoveryModeDisabledNoAdvances')
      }
      return ''
    },

    hideDefaultRecoveryModeOptions () {
      return (!this.isPayable || (!this.isDynamicCounterparty && !this.hasAdvances) || this.isPenalty)
    }
  },

  async created () {
    await this.fetchAccounts()
    await this.fetchAccountTags()
    this.setInitialState()
  },

  methods: {
    ...mapActions('account', ['fetchAccounts']),
    ...mapActions('tags', ['fetchAccountTags']),
    selectAccount (account) {
      this.selectedAccount = account
    },

    mappedRecoveryModes () {
      return RecoveryMode.enums.map(recoveryMode => ({
        ...recoveryMode,
        name: recoveryMode.name,
        definition: recoveryMode.explanation,
        excluded: (recoveryMode.value === RecoveryMode.Specific.value && this.isDynamicCounterparty)
      })).filter(recoveryMode => !recoveryMode.excluded)
    },

    saveBtnClicked () {
      this.$emit('bundle-template-updated', this.currentSelection, this.limitToBaseRate)
    },

    setInitialState () {
      if (this.propBuilder) {
        this.selectedRuntimeField = this.propBuilder.runtimeField ?? undefined
        this.selectedDefaultRecoveryMode = this.propBuilder.defaultRecoveryMode ?? RecoveryMode.None.value
        this.selectedAccount = (this.propBuilder.counterpartyId)
          ? { accountId: this.propBuilder.counterpartyId, name: this.propBuilder.counterpartyName, hasAdvances: this.propBuilder.counterpartyHasAdvances }
          : undefined
        this.limitToBaseRate = this.propBuilder.limitToBaseRate ?? false
      } else {
        this.selectedRuntimeField = undefined
        this.selectedDefaultRecoveryMode = RecoveryMode.None.value
        this.selectedAccount = undefined
        this.limitToBaseRate = false
      }
      this.selectedCounterpartyType = (this.selectedAccount !== undefined || (this.propBuilder && !this.selectedAccount && this.selectedRuntimeField == null))
        ? COUNTERPARTY_TYPE_ACCOUNT
        : COUNTERPARTY_TYPE_RUNTIME_FIELD
    }
  },

  watch: {
    selectedCounterpartyType (newVal) {
      if (newVal === COUNTERPARTY_TYPE_ACCOUNT) {
        this.selectedRuntimeField = undefined
        this.selectedDefaultRecoveryMode = this.propBuilder?.defaultRecoveryMode ?? RecoveryMode.None.value
      } else if (newVal === COUNTERPARTY_TYPE_RUNTIME_FIELD) {
        this.selectedRuntimeField = this.propBuilder?.runtimeField ?? (this.isAccrual ? RuntimeCounterparty.DefaultLogger.value : RuntimeCounterparty.Landowner.value)
        this.selectedDefaultRecoveryMode = this.propBuilder?.defaultRecoveryMode ?? RecoveryMode.None.value
        if (this.selectedDefaultRecoveryMode === RecoveryMode.Specific.value) {
          this.selectedDefaultRecoveryMode = RecoveryMode.None.value
        }
      }
    },

    hideDefaultRecoveryModeOptions (newVal, oldVal) {
      if (newVal) {
        this.selectedDefaultRecoveryMode = RecoveryMode.None.value
      } else if (oldVal) {
        this.selectedDefaultRecoveryMode = this.propBuilder?.defaultRecoveryMode ?? RecoveryMode.None.value
      }
    }
  }
}
</script>
